import { tahitiEnvironmentApi } from '@/lib/api';
import { isSettingsShortcut } from '@/lib/settings-shortcut';
import { type TahitiEnvironmentValue, tahitiEnvironmentValueSchema } from '@mss_creator/shared';
import { ActionBar, Alert, Button, OptionTile } from '@pms-digital/design-system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

const ENVIRONMENTS: readonly { value: TahitiEnvironmentValue; label: string }[] = [
  { value: 'prod', label: 'Prod' },
  { value: 'dev', label: 'Dev' },
];

function reloadLogin(): void {
  window.location.reload();
}

interface LoginTahitiPanelProps {
  /** Called once a change is applied; the login page reloads by default. */
  onApplied?: () => void;
}

/**
 * The hidden Tahiti environment switch on `/login`. Invisible until
 * Ctrl/Cmd+Shift+. is pressed (the same shortcut closes it, as does Escape).
 * It reads the active value from the public `GET /tahiti-environment` and
 * writes through `PUT /tahiti-environment`, with no session: nobody can sign in
 * to reach the settings page when the wrong Tahiti is selected. A change asks
 * for confirmation first (it signs everyone out); choosing the active value
 * does nothing. Built from the design system's components only.
 */
export function LoginTahitiPanel({ onApplied = reloadLogin }: LoginTahitiPanelProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<TahitiEnvironmentValue | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const environmentQuery = useQuery({
    queryKey: ['tahiti-environment'],
    queryFn: tahitiEnvironmentApi.get,
    enabled: open,
  });
  const mutation = useMutation({
    mutationFn: (value: TahitiEnvironmentValue) => tahitiEnvironmentApi.set(value),
    onSuccess: () => {
      setOpen(false);
      setPending(null);
      onApplied();
    },
  });
  const { reset: resetMutation } = mutation;

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (isSettingsShortcut(event)) {
        event.preventDefault();
        setOpen((previous) => !previous);
      } else if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setPending(null);
      resetMutation();
    }
  }, [open, resetMutation]);

  useEffect(() => {
    if (pending !== null) cancelRef.current?.focus();
  }, [pending]);

  if (!open) return null;

  const active = environmentQuery.data?.value;
  const activeLabel = ENVIRONMENTS.find((environment) => environment.value === active)?.label;
  const pendingLabel = ENVIRONMENTS.find((environment) => environment.value === pending)?.label;

  function choose(value: string): void {
    const parsed = tahitiEnvironmentValueSchema.safeParse(value);
    if (!parsed.success || parsed.data === active) return;
    resetMutation();
    setPending(parsed.data);
  }

  return (
    <fieldset
      className="m-0 mt-6 flex min-w-0 flex-col gap-3 border-0 border-gray-200 border-t p-0 pt-4"
      aria-label="Environnement Tahiti"
    >
      <legend className="sr-only">Environnement Tahiti</legend>
      <p className="m-0 font-semibold text-gray-700 text-small">Environnement Tahiti</p>
      <div className="grid grid-cols-2 gap-2">
        {ENVIRONMENTS.map((environment) => (
          <OptionTile
            key={environment.value}
            type="radio"
            name="tahiti-environment"
            value={environment.value}
            label={environment.label}
            checked={active === environment.value}
            disabled={active === undefined || mutation.isPending}
            onChange={() => choose(environment.value)}
          />
        ))}
      </div>
      {pending === null ? (
        <p className="m-0 text-caption text-gray-600">
          {environmentQuery.isError
            ? "Impossible de lire l'environnement actif."
            : `Valeur active : ${activeLabel ?? '…'}.`}{' '}
          Échap ou Ctrl+Shift+. pour fermer.
        </p>
      ) : (
        <>
          <Alert tone="warning">
            {`Passer en ${pendingLabel} ? Tous les utilisateurs seront déconnectés. Confirmer ?`}
          </Alert>
          {mutation.isError && (
            <Alert tone="danger">Le changement a échoué. Rien n'a été modifié.</Alert>
          )}
          <ActionBar
            secondary={
              <Button
                ref={cancelRef}
                variant="ghost"
                size="sm"
                disabled={mutation.isPending}
                onClick={() => setPending(null)}
              >
                Annuler
              </Button>
            }
            primary={
              <Button
                variant="secondary"
                size="sm"
                loading={mutation.isPending}
                onClick={() => mutation.mutate(pending)}
              >
                Confirmer
              </Button>
            }
          />
        </>
      )}
    </fieldset>
  );
}
